import { DragEvent, FC, useEffect, useState } from 'react'
import { useMount } from 'react-use'
import useCourseChapters from '@hooks/useCouseChapters'
import useMe from '@hooks/useMe'
import { useReorderCourseChaptersMutation } from '@services/courseChapters'
import { useGetCourseByIdQuery } from '@services/courses'
import { createInitFilter } from '@utils/filters'
import manyWords from '@utils/manyWords'
import { CourseDetailed } from '@models/course'
import { CourseChapter } from '@models/courseChapter'
import EditModal from '@templates/EditModal'
import { AccordionBodyProps } from '@organisms/Accordion'
import ModalContent from '@modals/CourseFormModal'
import AddChapterButton from './AddChapterButton'
import Chapter from './Chapter'
import DescriptionLink from './DescriptionLink'
import PublishButton from './PublishButton'
import { FooterWrapper, FinalTestRow, StyledAccordionTitle } from './styled'

type Props = {
  id: number
  expandedChapters: Set<number>
  onExpanded: (index: number) => void
} & AccordionBodyProps

const Body: FC<Props> = ({
  onChangeSize,
  toggleLoading,
  id,
  expandedChapters,
  onExpanded,
}) => {
  const { data: courseData } = useGetCourseByIdQuery(id)
  const course = courseData as CourseDetailed
  const { isAdmin } = useMe()
  const { data } = useCourseChapters(createInitFilter('course', id))
  const { items = [] } = data || {}

  const [reorderChapters] = useReorderCourseChaptersMutation()
  const [localChapters, setLocalChapters] = useState<CourseChapter[]>([])
  const [draggedChapterIndex, setDraggedChapterIndex] = useState<number | null>(
    null
  )

  useMount(() => {
    toggleLoading(true)
  })

  useEffect(() => {
    if (data) {
      toggleLoading(false)
      onChangeSize()
      setLocalChapters(data.items)
    }
  }, [data])

  const handleChapterDragStart = (
    e: DragEvent<HTMLElement>,
    chapterIndex: number
  ) => {
    setDraggedChapterIndex(chapterIndex)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', String(chapterIndex))
  }

  const handleChapterDragOver = (e: DragEvent<HTMLElement>) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleChapterDrop = async (
    e: DragEvent<HTMLElement>,
    targetIndex: number
  ) => {
    e.preventDefault()

    if (draggedChapterIndex === null || draggedChapterIndex === targetIndex) {
      setDraggedChapterIndex(null)

      return
    }

    const prevChapters = localChapters
    const newChapters = [...localChapters]
    const [moved] = newChapters.splice(draggedChapterIndex, 1)
    newChapters.splice(targetIndex, 0, moved)

    setLocalChapters(newChapters)
    setDraggedChapterIndex(null)

    const chapterIds = newChapters.map((chapter) => chapter.id)

    try {
      await reorderChapters({ courseId: id, chapterIds }).unwrap()
    } catch {
      setLocalChapters(prevChapters)
    }
  }

  const handleChapterDragEnd = () => {
    setDraggedChapterIndex(null)
  }

  if (!course) return null

  const finalTest = course.finalTest
  const finalTestHours = finalTest?.hoursTime
    ? Math.round(finalTest.hoursTime / 60)
    : 0

  const chapters = localChapters.length > 0 ? localChapters : items

  return (
    <>
      {chapters.length > 0 && (
        <StyledAccordionTitle mySize="h2">Разделы</StyledAccordionTitle>
      )}
      {chapters.map((item, index) => (
        <Chapter
          {...item}
          initialExpanded={expandedChapters.has(index)}
          index={index}
          onExpanded={onExpanded}
          onChangeSize={onChangeSize}
          courseIri={courseData?.['@id']!}
          key={item.id}
          draggable={isAdmin}
          isDragging={draggedChapterIndex === index}
          onDragStart={(e: DragEvent<HTMLElement>) =>
            handleChapterDragStart(e, index)
          }
          onDragEnd={handleChapterDragEnd}
          onDragOver={handleChapterDragOver}
          onDrop={(e: DragEvent<HTMLElement>) => handleChapterDrop(e, index)}
        />
      ))}
      {finalTest && (
        <FinalTestRow>
          <span>Итоговое тестирование</span>
          {finalTestHours > 0 && (
            <span>{manyWords(finalTestHours, 'час', 'часа', 'часов')}</span>
          )}
        </FinalTestRow>
      )}
      {isAdmin && (
        <FooterWrapper>
          <EditModal
            ModalContent={ModalContent}
            modalProps={{ course }}
            editText="Редактировать курс"
          />
          <AddChapterButton courseIri={course?.['@id']} />
          <DescriptionLink descriptionLink={course?.descriptionLink} />
          <PublishButton course={course} />
        </FooterWrapper>
      )}
    </>
  )
}

export default Body
